import React, { useState, useCallback, useDeferredValue, useMemo, useRef, useEffect } from 'react';
import { Table, Card, message, Spin, Select, Tabs, Tooltip, Button } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, DownOutlined, RightOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showError } from '../../../utils/exceptionUtils';
import { useNavigate } from 'react-router-dom';
import TimeRangeFilter, { TimeRange } from '../../../components/dashboard/TimeRangeFilter';
import { ScaleQueryType } from '../../../graphql/types/dashboard/ScaleDashboardTypes';
import MetricTooltip from '../../../components/dashboard/MetricTooltip';
import { QUERY_DELIVERY_DASHBOARD, QUERY_DELIVERY_DASHBOARD_SUMMARY } from '../../../graphql/queries/dashboard/queryDeliveryDashboard';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DASHBOARD_PACIFIC_TZ, formatHubDisplayLabel } from '../../../utils/dashboardPacificTime';
import {
    DASHBOARD_EXCLUDE_HUB_LABELS,
    useOperableHubSelectOptions,
} from '../../../hooks/useOperableHubSelectOptions';
import {
    DeliveryDashboardItem,
    DeliveryDashboardQueryInput,
    QueryDeliveryDashboardResponse,
    QueryDeliveryDashboardSummaryResponse,
} from '../../../graphql/types/dashboard/DeliveryDashboardTypes';
import { WarehouseBusinessType } from '../../../graphql/types/dashboard/SortingDashboardTypes';
import { formatUtcToPacific } from '../../../utils/timezoneFormatter';
import DspRankingPage from './DspRankingPage';
import {
    GENERATE_DASHBOARD_TABLE_EXPORT,
    GenerateDashboardTableExportResponse,
    GenerateDashboardTableExportVariables,
} from '../../../graphql/mutations/dashboard/generateDashboardTableExport';
import { dashboardTableExportCellString } from '../../../utils/dashboardTableExportCellString';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';
import MetricAnnotationCell from '../../../components/dashboardAnnotation/MetricAnnotationCell';
import { useDashboardAnnotations } from '../../../components/dashboardAnnotation/useDashboardAnnotations';
import { buildAnnotationKey } from '../../../components/dashboardAnnotation/buildAnnotationKey';
import {
    dashboardAnnotationRegistry,
    PAGE_KEY_DELIVERY,
    type CellContext,
    type DashboardAnnotationColumnType,
} from '../../../components/dashboardAnnotation/registry';
import DashboardMarkLegend from '../../../components/dashboard/DashboardMarkLegend';
import { useDashboardMarkRules } from '../../../hooks/useDashboardMarkRules';
import { DASHBOARD_MARK_CONFIG_KEY } from '../../../graphql/types/dashboard/DashboardMarkRuleTypes';
import {
    appendTargetValueDesc,
    CellMark,
    markCellClassName,
    NO_CELL_MARK,
    resolveCellMark,
} from '../../../utils/dashboardMarkRule';

dayjs.extend(utc);
dayjs.extend(timezone);

// 格式化数字：整数不带小数，小数保留1位，添加千分位分隔符
const formatNumber = (num: number | undefined | null): string => {
    if (num === undefined || num === null || isNaN(num)) {
        return '0';
    }
    
    if (Number.isInteger(num)) {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    
    const rounded = Math.round(num * 10) / 10;
    // 如果是整数，不显示.0
    if (Number.isInteger(rounded)) {
        return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    const fixed = rounded.toFixed(1);
    const [integerPart, decimalPart] = fixed.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${formattedInteger}.${decimalPart}`;
};

// 格式化百分比：尾程看板直接使用后端返回的两位小数字符串；仅做空值/容错处理
const formatPercentage = (rate?: string | null, metricKey?: string): string => {
    if (rate === undefined || rate === null || rate === '') return '-';
    if (rate === '-') return '-';

    // 后端已经返回形如 "xx.xx%" 的字符串，直接透传
    if (typeof rate === 'string') {
        const trimmed = rate.trim();
        return trimmed === '' ? '-' : trimmed;
    }

    // 兼容极端情况：如果传入的是数值，则按两位小数格式化为百分比
    const num = typeof rate === 'number' ? rate : Number(rate);
    if (isNaN(num)) return '-';
    return `${num.toFixed(2)}%`;
};

/**
 * 参与标记的指标 → 日期列 dataIndex 前缀。
 * 列 key 形如 `pickup12h_2026-08-01`，与行数据里的字段名一致。
 */
const DELIVERY_MARK_COLUMN_PREFIX: Record<string, string> = {
    pickupRate: 'pickup',
    deliveredRate: 'delivered',
    pickup12hRate: 'pickup12h',
    delivered24hRate: 'delivered24h',
    undeliveredRate: 'undelivered',
    undeliveredUnpickupRate: 'unpickup_rate',
};

/** 图例中的指标简称 */
const DELIVERY_MARK_METRIC_LABELS: Record<string, string> = {
    pickupRate: '领取率',
    deliveredRate: '领取妥投率',
    pickup12hRate: '到货后12h领取率',
    delivered24hRate: '领取后24h妥投率',
    undeliveredRate: '最终未妥投包裹率',
    undeliveredUnpickupRate: '最终未领取率',
};

/**
 * NDR「未领取」子指标的标记字段名，与后端 dashboard.mark.delivery 配置一致。
 * 该率后端无对应字段，是前端按 unpickupCount / arrivedCount 现算的，取上周同日需特殊处理。
 */
const UNPICKUP_MARK_FIELD = 'undeliveredUnpickupRate';

interface TableDataRow {
    key: string;
    metricName: string;
    metricTooltip?: string; // 指标提示文本
    isExpandable: boolean;
    isDetail: boolean;
    hubId?: number;
    warehouseId?: number;
    dspId?: number;
    sortingCode?: string;
    /** 用于「指标」列按 hub 名称排序：hub 行为该 hub 显示名，明细行为所属 hub 显示名 */
    sortKey?: string;
    /** 同 sortKey 时保持顺序：hub 行 0，明细行 1、2… */
    sortRank?: number;
    /**
     * 该行所有祖先行的 key（HUB 行为空，WHS 行为 [hubKey]，DSP 行为 [hubKey, warehouseKey]）。
     * 全部祖先都处于展开态时该行才可见。行的构建因此与展开状态解耦。
     */
    ancestorKeys: string[];
    [key: string]: any;
}

interface ColumnGroup {
    hubId: number;
    warehouseId: number;
    sortingCode?: string;
    warehouses: Array<{
        warehouseId: number;
        sortingCode?: string;
        dsps?: Array<{ dspId: number; dspName?: string }>;
    }>;
}

/**
 * 看板指标注释：非环比 period 直接来自后端 timePeriod（DAILY 为单日日期，WEEKLY/MONTHLY 为 week/month range 字符串），直接作 dateKey。
 * 环比分支目前不可达——后端 queryDeliveryDashboard 从未返回过含"环比"的 timePeriod（无 T-7/环比计算实现），此处仅作防御性占位，endDate 是否为最终正确签名待该功能实际落地时再定。
 */
function computeDeliveryAnnotationKey(
    period: string,
    timeRange: TimeRange | null
): { columnType: DashboardAnnotationColumnType; dateKey: string } {
    if (period.includes('环比')) {
        return { columnType: 'WOW', dateKey: timeRange?.endDate ?? '' };
    }
    return { columnType: 'RAW', dateKey: period };
}

const deliveryAnnotationConfig = dashboardAnnotationRegistry[PAGE_KEY_DELIVERY];

/**
 * NDR 剔除近3自然日：日视图隐藏 T/T-1/T-2/T-3（美西），T-4 起展示；周/月不过滤。
 * 「最近3个自然日」按产品口径为 T-1/T-2/T-3（不含当天），故 cutoff 取 T-4。
 */
function shouldHidePeriodForNdr(period: string, queryType: ScaleQueryType): boolean {
    if (queryType !== ScaleQueryType.DAILY) return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(period)) return false;
    const cutoffExclusive = dayjs().tz(DASHBOARD_PACIFIC_TZ).startOf('day').subtract(4, 'day').format('YYYY-MM-DD');
    return period > cutoffExclusive;
}

/** NDR 未妥投原因拆分：页面列与导出列共用，避免两处定义漂移 */
const NDR_REASONS = [
    // 仅「未领取」参与单元格标记，故只有它带目标值说明（markField 指向取目标值的规则）
    { key: 'unpickup', label: '未领取', tooltip: '未领取包裹数 / 到货包裹数', markField: UNPICKUP_MARK_FIELD, dataKey: 'undelivered_unpickup_rate', countTotalKey: 'unpickup_count', countKey: 'unpickup_count_', rateKey: 'unpickup_rate_' },
    { key: 'delivering', label: '派送中', dataKey: 'undelivered_delivering_rate', countTotalKey: 'undelivered_delivering', countKey: 'delivering_count_', rateKey: 'delivering_rate_' },
    { key: 'deliveryFailed', label: '派送失败/返仓', dataKey: 'undelivered_delivery_failed_rate', countTotalKey: 'undelivered_delivery_failed', countKey: 'deliveryFailed_count_', rateKey: 'deliveryFailed_rate_' },
    { key: 'return', label: '退件', dataKey: 'undelivered_return_rate', countTotalKey: 'undelivered_return', countKey: 'return_count_', rateKey: 'return_rate_' },
    { key: 'lostDamaged', label: '丢失/破损', dataKey: 'undelivered_lost_damaged_rate', countTotalKey: 'undelivered_lost_damaged', countKey: 'lostDamaged_count_', rateKey: 'lostDamaged_rate_' },
];

const DeliveryDataPage: React.FC = () => {
    const { t } = useTranslation();
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const navigate = useNavigate();
    const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
    // 默认展开所有Hub（会在数据加载后动态更新）
    const [expandedHubs, setExpandedHubs] = useState<Set<string>>(new Set());
    /**
     * 展开态的最新快照，供列定义的 render 读取。
     *
     * 展开箭头只是「指标」列里的一个图标，若让 columns 直接依赖 expandedHubs，
     * 每次展开/折叠都要重建全部列（长日期范围下数百列），点箭头会明显卡顿。
     * 改走 ref：expandedHubs 变化会经 tableData 触发重渲染，render 执行时能读到最新值。
     */
    const expandedHubsRef = useRef(expandedHubs);
    expandedHubsRef.current = expandedHubs;
    const [selectedHubId, setSelectedHubId] = useState<number>(0);
    /** 按 hub 名称排序：仅在「全美」单元格点击时切换，null 表示默认顺序 */
    const [hubNameSortOrder, setHubNameSortOrder] = useState<'ascend' | 'descend' | null>(null);
    /** 按到货包裹数合计排序：仅在「合计」列标题点击时切换，null 表示默认顺序 */
    const [totalArrivedSortOrder, setTotalArrivedSortOrder] = useState<'ascend' | 'descend' | null>(null);
    /** 各指标是否展开日期列（到货包裹数、领取率等下的各日期子列），收起时只显示合计列 */
    const [dateColumnsExpanded, setDateColumnsExpanded] = useState<{
        arrived: boolean;
        pickup: boolean;
        delivered: boolean;
        inboundMissScan: boolean;
        pickup12h: boolean;
        delivered24h: boolean;
        returnUnfinished72h: boolean;
        returnWarehouseNoPickup24h: boolean;
        undeliveredRate: boolean;
        undeliveredCount: boolean;
        undeliveredRateDates: boolean;
        undeliveredCountDates: boolean;
        undeliveredRate_unpickup: boolean;
        undeliveredRate_delivering: boolean;
        undeliveredRate_deliveryFailed: boolean;
        undeliveredRate_return: boolean;
        undeliveredRate_lostDamaged: boolean;
        undeliveredCount_unpickup: boolean;
        undeliveredCount_delivering: boolean;
        undeliveredCount_deliveryFailed: boolean;
        undeliveredCount_return: boolean;
        undeliveredCount_lostDamaged: boolean;
    }>({
        arrived: true,
        pickup: true,
        delivered: true,
        inboundMissScan: true,
        pickup12h: true,
        delivered24h: true,
        returnUnfinished72h: true,
        returnWarehouseNoPickup24h: true,
        undeliveredRate: true,
        undeliveredCount: true,
        undeliveredRateDates: true,
        undeliveredCountDates: true,
        undeliveredRate_unpickup: false,
        undeliveredRate_delivering: false,
        undeliveredRate_deliveryFailed: false,
        undeliveredRate_return: false,
        undeliveredRate_lostDamaged: false,
        undeliveredCount_unpickup: false,
        undeliveredCount_delivering: false,
        undeliveredCount_deliveryFailed: false,
        undeliveredCount_return: false,
        undeliveredCount_lostDamaged: false,
    });

    const { hubOptions, loading: hubsLoading, singleAccessibleHubId } = useOperableHubSelectOptions({
        excludeLabels: [...DASHBOARD_EXCLUDE_HUB_LABELS],
        filterBusinessTypes: [WarehouseBusinessType.LAST_MILE],
    });

    useEffect(() => {
        if (singleAccessibleHubId != null) {
            setSelectedHubId(singleAccessibleHubId);
        }
    }, [singleAccessibleHubId]);

    // GraphQL查询 - 前6个指标
    const { data: dashboardData, loading: dashboardLoading } = useQuery<QueryDeliveryDashboardResponse>(
        QUERY_DELIVERY_DASHBOARD,
        {
            variables: {
                input: {
                    queryType: timeRange?.queryType,
                    startDate: timeRange?.startDate,
                    endDate: timeRange?.endDate,
                    hubId: selectedHubId,
                } as DeliveryDashboardQueryInput,
            },
            skip: !timeRange,
            onError: (error) => {
                console.error('查询尾程数据失败:', error);
                message.error('查询尾程数据失败，请稍后重试');
            },
        }
    );

    // GraphQL查询 - 后2个指标（汇总数据）
    const { data: summaryData, loading: summaryLoading } = useQuery<QueryDeliveryDashboardSummaryResponse>(
        QUERY_DELIVERY_DASHBOARD_SUMMARY,
        {
            variables: {
                input: {
                    queryType: timeRange?.queryType,
                    startDate: timeRange?.startDate,
                    endDate: timeRange?.endDate,
                    hubId: selectedHubId,
                } as DeliveryDashboardQueryInput,
            },
            skip: !timeRange,
            onError: (error) => {
                console.error('查询尾程数据汇总失败:', error);
                message.error('查询尾程数据汇总失败，请稍后重试');
            },
        }
    );

    const loading = dashboardLoading || summaryLoading;

    const dataUpdatedAtText = (() => {
        const calculatedAt = summaryData?.queryDeliveryDashboardSummary?.calculatedAt;
        if (!calculatedAt) return '';
        return formatUtcToPacific(calculatedAt);
    })();

    const handleTimeRangeChange = useCallback((newTimeRange: TimeRange) => {
        setTimeRange(newTimeRange);
    }, []);

    const handleHubChange = useCallback((value: number) => {
        setSelectedHubId(value);
    }, []);

    // 点击 HUB 名称 -> hub-parcel-sla-monitoring（hubId + 日期）；点击 WHS 名称 -> parcel-sla-monitoring（warehouseId + 日期）；从看板跳转时默认美西时区
    const handleNameClick = useCallback(
        (record: TableDataRow) => {
            const start = timeRange?.startDate ?? new Date().toISOString().split('T')[0];
            const end = timeRange?.endDate ?? timeRange?.startDate ?? new Date().toISOString().split('T')[0];
            const params = new URLSearchParams();
            params.set('arrivalDateStart', start);
            params.set('arrivalDateEnd', end);
            params.set('timezone', '1'); // 1=美西，目标页据此默认美西时区
            if (record.warehouseId !== 0 && record.warehouseId != null) {
                params.set('warehouseIds', String(record.warehouseId));
                navigate(`/wms/delivery/parcel-sla-monitoring?${params.toString()}`);
            } else if (record.hubId != null && record.hubId !== 0) {
                params.set('hubId', String(record.hubId));
                navigate(`/wms/progress/hub-parcel-sla-monitoring?${params.toString()}`);
            }
        },
        [navigate, timeRange?.startDate, timeRange?.endDate]
    );

    // 切换展开状态
    const toggleExpand = (key: string) => {
        setExpandedHubs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    /** 后端返回的合计行 timePeriod（英文避免编码问题），率类合计 = 多天分子之和/多天分母之和，不参与日期列 */
    const TIME_PERIOD_TOTAL = 'total';

    // 从接口数据中提取 timePeriod 并去重（排除「合计」，合计列单独在率指标最左侧展示）
    const timePeriods = useMemo(() => {
        if (!dashboardData?.queryDeliveryDashboard) {
            return [];
        }
        const periods = Array.from(
            new Set(
                dashboardData.queryDeliveryDashboard
                    .map(item => item.timePeriod)
                    .filter(p => p !== TIME_PERIOD_TOTAL)
            )
        );
        // 将包含"环比"的周期放到最后
        const compareIndex = periods.findIndex(p => p.includes('环比'));
        if (compareIndex !== -1) {
            const comparePeriod = periods[compareIndex];
            return [...periods.filter(p => !p.includes('环比')), comparePeriod];
        }
        return periods;
    }, [dashboardData]);

    const markCtx = useDashboardMarkRules(
        DASHBOARD_MARK_CONFIG_KEY.DELIVERY,
        timeRange?.queryType === ScaleQueryType.DAILY
    );

    /**
     * 红框需要「标记列的上周同日」，而本看板默认范围是「昨日」单日，上周同日几乎必然落在范围外，
     * 故补一次小范围查询（仅覆盖所需的那几天）。已在主查询范围内的日期不重复请求。
     */
    const lastWeekRange = useMemo(() => {
        if (!markCtx.enabled || markCtx.requiredLastWeekDates.length === 0) return null;
        const missing = markCtx.requiredLastWeekDates.filter((d) => !timePeriods.includes(d));
        if (missing.length === 0) return null;
        const sorted = [...missing].sort();
        return { startDate: sorted[0], endDate: sorted[sorted.length - 1] };
    }, [markCtx, timePeriods]);

    const { data: lastWeekData } = useQuery<QueryDeliveryDashboardResponse>(QUERY_DELIVERY_DASHBOARD, {
        variables: {
            input: {
                queryType: ScaleQueryType.DAILY,
                startDate: lastWeekRange?.startDate,
                endDate: lastWeekRange?.endDate,
                hubId: selectedHubId,
            } as DeliveryDashboardQueryInput,
        },
        skip: !lastWeekRange,
        fetchPolicy: 'cache-first',
    });

    /**
     * 上周同日数据索引。key 必须含 dspId：HUB/WHS 汇总行是 dspId=-1，DSP 行是各自的 id，
     * 少了这一维会让 DSP 行取到同 WHS 汇总值，周环比红框据此算出的就是错的。
     */
    const lastWeekItemIndex = useMemo(() => {
        const map = new Map<string, DeliveryDashboardItem>();
        (lastWeekData?.queryDeliveryDashboard ?? []).forEach((item) => {
            map.set(`${item.hubId}-${item.warehouseId}-${item.dspId}-${item.timePeriod}`, item);
        });
        return map;
    }, [lastWeekData]);

    /**
     * 计算单元格标记。上周同日优先取本行已加载的列，取不到再查补充请求的结果。
     */
    const resolveMarkForCell = useCallback(
        (fieldName: string, period: string, record: TableDataRow): CellMark => {
            const prefix = DELIVERY_MARK_COLUMN_PREFIX[fieldName];
            if (!prefix) return NO_CELL_MARK;

            const lastWeekDate = markCtx.lastWeekDateByField.get(fieldName);
            let lastWeekValue: unknown = lastWeekDate
                ? (record as Record<string, unknown>)[`${prefix}_${lastWeekDate}`]
                : undefined;
            if (lastWeekValue === undefined && lastWeekDate) {
                const item = lastWeekItemIndex.get(
                    `${record.hubId}-${record.warehouseId ?? 0}-${record.dspId ?? -1}-${lastWeekDate}`
                );
                if (item && fieldName === UNPICKUP_MARK_FIELD) {
                    // 未领取率后端无字段，按页面同一公式现算；到货数为 0 时视为无数据，不标红框
                    const arrived = Number(item.arrivedCount) || 0;
                    lastWeekValue =
                        arrived > 0 ? ((Number(item.unpickupCount) || 0) / arrived) * 100 : undefined;
                } else {
                    lastWeekValue = item
                        ? (item as unknown as Record<string, unknown>)[fieldName]
                        : undefined;
                }
            }

            const isNation = record.metricName === '全美' && !record.isDetail;
            const isWarehouse = (record.warehouseId ?? 0) > 0;
            return resolveCellMark({
                ctx: markCtx,
                fieldName,
                columnDate: period,
                row: {
                    isNation,
                    hubLabel: isNation || isWarehouse ? undefined : record.metricName,
                    isWarehouse,
                },
                cellValue: (record as Record<string, unknown>)[`${prefix}_${period}`],
                lastWeekValue,
            });
        },
        [markCtx, lastWeekItemIndex]
    );

    // 构建列分组结构
    const columnGroups = useMemo(() => {
        if (!dashboardData?.queryDeliveryDashboard) {
            return [];
        }

        const rawData = dashboardData.queryDeliveryDashboard;

        const hubGroups = new Map<number, ColumnGroup>();
        
        // 处理所有数据，分离Hub汇总、Warehouse明细、DSP明细（hubId 统一为 number 避免 0/'0' 重复）
        rawData.forEach(item => {
            const hubId = Number(item.hubId);
            const warehouseId = Number(item.warehouseId);
            const dspId = Number(item.dspId);

            if (!hubGroups.has(hubId)) {
                hubGroups.set(hubId, {
                    hubId,
                    warehouseId: 0,
                    sortingCode: undefined,
                    warehouses: []
                });
            }

            const hubGroup = hubGroups.get(hubId)!;

            if (warehouseId === 0) {
                // Hub汇总行（dspId=-1，不涉及DSP）
                hubGroup.sortingCode = item.sortingCode;
            } else if (dspId === -1) {
                // Warehouse明细行（dspId=-1，不涉及DSP）：查找或创建对应的warehouse条目（可能已被同warehouse下的DSP行提前创建）
                let warehouseEntry = hubGroup.warehouses.find(w => w.warehouseId === warehouseId);
                if (!warehouseEntry) {
                    warehouseEntry = { warehouseId, sortingCode: item.sortingCode, dsps: [] };
                    hubGroup.warehouses.push(warehouseEntry);
                } else {
                    warehouseEntry.sortingCode = item.sortingCode;
                }
            } else {
                // DSP明细行：挂载到所属warehouse的dsps[]下，warehouse条目可能尚未创建（行序不保证）
                let warehouseEntry = hubGroup.warehouses.find(w => w.warehouseId === warehouseId);
                if (!warehouseEntry) {
                    warehouseEntry = { warehouseId, sortingCode: undefined, dsps: [] };
                    hubGroup.warehouses.push(warehouseEntry);
                }
                if (!warehouseEntry.dsps) {
                    warehouseEntry.dsps = [];
                }
                if (!warehouseEntry.dsps.some(d => d.dspId === dspId)) {
                    warehouseEntry.dsps.push({ dspId, dspName: item.dspName });
                }
            }
        });

        // 与表头列顺序一致：环比放最后，第一个日期列为排序依据
        const periods = Array.from(new Set(rawData.map((item: DeliveryDashboardItem) => item.timePeriod)));
        const comparePeriod = periods.find((p: string) => String(p).includes('环比'));
        const orderedPeriods = comparePeriod
            ? [...periods.filter((p: string) => !String(p).includes('环比')), comparePeriod]
            : periods;
        const firstPeriod = orderedPeriods.find((p: string) => !String(p).includes('环比')) ?? orderedPeriods[0];
        // getArrived 在三层排序的比较器里被反复调用，逐次 find 会对 rawData 做 O(n log n) 次全量扫描，故先建索引
        const arrivedIndex = new Map<string, number>();
        rawData.forEach((i: DeliveryDashboardItem) => {
            const key = `${i.timePeriod}|${Number(i.hubId)}|${Number(i.warehouseId)}|${Number(i.dspId)}`;
            if (arrivedIndex.has(key)) return;
            const v = i.arrivedCount;
            arrivedIndex.set(key, typeof v === 'number' ? v : Number(v) || 0);
        });
        const getArrived = (hubId: number, warehouseId: number, period: string, dspId: number = -1): number =>
            arrivedIndex.get(`${period}|${hubId}|${warehouseId}|${dspId}`) ?? 0;

        const groups = Array.from(hubGroups.values());
        // 全美(hubId=0)第一行，其余 hub 按「第一个日期列」的到货包裹数倒序
        groups.sort((a, b) => {
            if (a.hubId === 0) return -1;
            if (b.hubId === 0) return 1;
            const aCount = firstPeriod ? getArrived(a.hubId, 0, firstPeriod) : 0;
            const bCount = firstPeriod ? getArrived(b.hubId, 0, firstPeriod) : 0;
            return bCount - aCount;
        });
        // 每个 hub 下的 warehouse 按「第一个日期列」的到货包裹数倒序
        groups.forEach(group => {
            group.warehouses.sort((wa, wb) => {
                const aCount = firstPeriod ? getArrived(group.hubId, wa.warehouseId, firstPeriod) : 0;
                const bCount = firstPeriod ? getArrived(group.hubId, wb.warehouseId, firstPeriod) : 0;
                return bCount - aCount;
            });
            // 每个 warehouse 下的 DSP 按「第一个日期列」的到货包裹数倒序
            group.warehouses.forEach(warehouseEntry => {
                warehouseEntry.dsps?.sort((da, db) => {
                    const aCount = firstPeriod ? getArrived(group.hubId, warehouseEntry.warehouseId, firstPeriod, da.dspId) : 0;
                    const bCount = firstPeriod ? getArrived(group.hubId, warehouseEntry.warehouseId, firstPeriod, db.dspId) : 0;
                    return bCount - aCount;
                });
            });
        });

        return groups;
    }, [dashboardData]);

    // 仅在数据/筛选变化时自动展开所有 Hub（用稳定 key 避免每次渲染都覆盖用户折叠）
    const hubListKey = useMemo(() => {
        if (!dashboardData?.queryDeliveryDashboard) return '';
        const raw = dashboardData.queryDeliveryDashboard;
        const hubIds = Array.from(new Set(raw.map((i: DeliveryDashboardItem) => Number(i.hubId)))).sort((a, b) => a - b);
        return hubIds.join(',');
    }, [dashboardData]);

    React.useEffect(() => {
        if (columnGroups.length > 0 && hubListKey) {
            const allHubKeys = columnGroups
                .filter(group => group.warehouses.length > 0)
                .map(group => `hub_${group.hubId}`);
            setExpandedHubs(new Set(allHubKeys));
        }
    }, [hubListKey]);

    // 看板指标注释：日期列 period 直接来自后端 timePeriod（含 WEEKLY/MONTHLY range 字符串）；环比分支同 computeDeliveryAnnotationKey 注释，当前不可达
    // timeRange 与 timePeriods 分步就绪，返回 [] 让 useDashboardAnnotations 的 skip 生效，避免刷新时打 3 次请求
    const annotationDateKeys = useMemo(() => {
        if (!timeRange?.startDate || !timeRange?.endDate || timePeriods.length === 0) {
            return [];
        }
        const rangeDateKey = `${timeRange.startDate}~${timeRange.endDate}`;
        const keys = new Set<string>([rangeDateKey]);
        timePeriods.forEach(period => {
            keys.add(period.includes('环比') ? timeRange.endDate : period);
        });
        return Array.from(keys);
    }, [timePeriods, timeRange]);
    const {
        annotationsByKey,
        createAnnotation: createDashboardAnnotation,
        updateAnnotation: updateDashboardAnnotation,
        deleteAnnotation: deleteDashboardAnnotation,
        refetchAnnotations: refetchDeliveryAnnotations,
    } = useDashboardAnnotations({ pageKey: PAGE_KEY_DELIVERY, dateKeys: annotationDateKeys });

    // 动态生成列配置（2级表头）
    const columns: ColumnsType<TableDataRow> = useMemo(() => {
        const wrapWithAnnotation = (
            metricKey: string,
            columnType: DashboardAnnotationColumnType,
            dateKey: string,
            record: TableDataRow,
            node: React.ReactNode
        ): React.ReactNode => {
            const annotationCellCtx: CellContext = {
                businessType: record.key,
                hubId: String(record.hubId ?? 0),
                metricKey,
                columnType,
                dateKey,
            };
            const annotationDims = deliveryAnnotationConfig.buildCellDimensions(annotationCellCtx);
            const cellAnnotations = annotationsByKey.get(buildAnnotationKey(PAGE_KEY_DELIVERY, annotationDims, annotationCellCtx.dateKey, annotationCellCtx.columnType, annotationCellCtx.hubId ?? '', annotationCellCtx.metricKey));
            return (
                <MetricAnnotationCell
                    hubId={annotationCellCtx.hubId ?? ''}
                    metricKey={annotationCellCtx.metricKey}
                    dimensions={annotationDims}
                    dateKey={annotationCellCtx.dateKey}
                    columnType={annotationCellCtx.columnType}
                    annotations={cellAnnotations}
                    writeResource={deliveryAnnotationConfig.writeResource}
                    adminResource={deliveryAnnotationConfig.adminResource}
                    onCreate={createDashboardAnnotation}
                    onUpdate={updateDashboardAnnotation}
                    onDelete={deleteDashboardAnnotation}
                    onOpen={refetchDeliveryAnnotations}
                >
                    {node}
                </MetricAnnotationCell>
            );
        };
        const annotationRangeDateKey = `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
        const cols: ColumnsType<TableDataRow> = [
            {
                title: '指标',
                dataIndex: 'metricName',
                key: 'metricName',
                fixed: 'left',
                width: 200,
                render: (text: string, record: TableDataRow) => {
                    const canNavigate =
                        record.dspId == null &&
                        ((record.warehouseId !== 0 && record.warehouseId != null) ||
                        (record.hubId != null && record.hubId !== 0));
                    const isNationwideRow = record.metricName === '全美' && !record.isDetail;
                    // DSP行比warehouse行多缩进一级，体现三层树形层级
                    const detailIndentClass = record.dspId != null ? 'pl-8' : 'pl-4';
                    const cycleHubSort = () => {
                        setHubNameSortOrder(prev => (prev === null ? 'descend' : prev === 'descend' ? 'ascend' : null));
                    };
                    return {
                        children: (
                            <div className="flex items-center gap-2">
                                {record.isExpandable && (
                                    <span
                                        className="cursor-pointer text-blue-500"
                                        onClick={() => toggleExpand(record.key)}
                                    >
                                        {expandedHubsRef.current.has(record.key) ? (
                                            <DownOutlined />
                                        ) : (
                                            <RightOutlined />
                                        )}
                                    </span>
                                )}
                                {canNavigate ? (
                                    <span
                                        className={`cursor-pointer text-purple-600 hover:text-purple-700 ${record.isDetail ? detailIndentClass : 'font-medium'}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleNameClick(record);
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleNameClick(record);
                                            }
                                        }}
                                    >
                                        {text}
                                    </span>
                                ) : (
                                    <span className={record.isDetail ? `text-gray-600 ${detailIndentClass}` : 'font-medium'}>
                                        {text}
                                    </span>
                                )}
                                {isNationwideRow && (
                                    <Tooltip title="按 hub 名称排序">
                                        <span
                                            className={`cursor-pointer ml-1 ${hubNameSortOrder ? 'text-blue-500 hover:text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTotalArrivedSortOrder(null);
                                                cycleHubSort();
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    cycleHubSort();
                                                }
                                            }}
                                        >
                                            {hubNameSortOrder === 'descend' ? (
                                                <CaretDownOutlined />
                                            ) : (
                                                <CaretUpOutlined />
                                            )}
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        ),
                        props: {
                            rowSpan: 1,
                        },
                    };
                },
            },
        ];

        // NDR 日期子列：过滤掉近3自然日（日视图），周/月不过滤
        const ndrTimePeriods = timePeriods.filter(p => !timeRange || !shouldHidePeriodForNdr(p, timeRange.queryType));

        // 指标1：到货包裹数（最左列为合计，带排序按钮 + 日期列展开/收起）
        const cycleTotalArrivedSort = () => {
            setHubNameSortOrder(null);
            setTotalArrivedSortOrder(prev => (prev === null ? 'descend' : prev === 'descend' ? 'ascend' : null));
        };
        const getToggleDateColumnsNode = (metricKey: keyof typeof dateColumnsExpanded) => (
            <Tooltip title={dateColumnsExpanded[metricKey] ? '收起日期列' : '展开日期列'}>
                <span
                    className="cursor-pointer text-gray-400 hover:text-gray-600 ml-0.5"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDateColumnsExpanded(prev => ({ ...prev, [metricKey]: !prev[metricKey] }));
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDateColumnsExpanded(prev => ({ ...prev, [metricKey]: !prev[metricKey] }));
                        }
                    }}
                >
                    {dateColumnsExpanded[metricKey] ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                </span>
            </Tooltip>
        );

        const getToggleSubColumnsNode = (metricKey: keyof typeof dateColumnsExpanded) => (
            <Tooltip title={dateColumnsExpanded[metricKey] ? '收起子列' : '展开子列'}>
                <span
                    className="cursor-pointer text-gray-400 hover:text-gray-600 ml-0.5"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDateColumnsExpanded(prev => ({ ...prev, [metricKey]: !prev[metricKey] }));
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDateColumnsExpanded(prev => ({ ...prev, [metricKey]: !prev[metricKey] }));
                        }
                    }}
                >
                    {dateColumnsExpanded[metricKey] ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                </span>
            </Tooltip>
        );
        cols.push({
            title: (
                <span>
                    到货包裹数
                    <MetricTooltip content="到达提货仓的包裹数，按首次到达日期归属" />
                </span>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            <Tooltip title="按合计排序">
                                <span
                                    className={`cursor-pointer ${totalArrivedSortOrder ? 'text-blue-500 hover:text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        cycleTotalArrivedSort();
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            cycleTotalArrivedSort();
                                        }
                                    }}
                                >
                                    {totalArrivedSortOrder === 'descend' ? (
                                        <CaretDownOutlined />
                                    ) : (
                                        <CaretUpOutlined />
                                    )}
                                </span>
                            </Tooltip>
                            {getToggleDateColumnsNode('arrived')}
                        </div>
                    ),
                    dataIndex: 'arrived_total',
                    key: 'arrived_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('arrivedCount', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatNumber(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.arrived
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `arrived_${period}`,
                        key: `arrived_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            return wrapWithAnnotation('arrivedCount', columnType, dateKey, record, (
                                <span>{formatNumber(value)}</span>
                            ));
                        },
                    }))
                    : []),
            ],
        });

        // 指标2：领取率（最左列为合计，后端多天分子之和/分母之和）
        cols.push({
            title: (
                <span>
                    领取率
                    <MetricTooltip content={appendTargetValueDesc('到货包裹中，截止目前已领取的比例值', markCtx, 'pickupRate')} />
                </span>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('pickup')}
                        </div>
                    ),
                    dataIndex: 'pickup_total',
                    key: 'pickup_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('pickupRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.pickup
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `pickup_${period}`,
                        key: `pickup_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            const textClass = resolveMarkForCell('pickupRate', period, record).redText
                                ? 'dashboard-mark-text-danger'
                                : undefined;
                            return wrapWithAnnotation('pickupRate', columnType, dateKey, record, (
                                <span className={textClass}>{formatPercentage(value)}</span>
                            ));
                        },
                        onCell: (record: TableDataRow) => ({
                            className: markCellClassName(resolveMarkForCell('pickupRate', period, record)),
                        }),
                    }))
                    : []),
            ],
        });

        // 指标3：领取妥投率（最左列为合计，后端多天分子之和/分母之和）
        cols.push({
            title: (
                <span>
                    领取妥投率
                    <MetricTooltip content={appendTargetValueDesc('到货且领取包裹中，截止目前已妥投的比例值', markCtx, 'deliveredRate')} />
                </span>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('delivered')}
                        </div>
                    ),
                    dataIndex: 'delivered_total',
                    key: 'delivered_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('deliveredRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.delivered
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `delivered_${period}`,
                        key: `delivered_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            const textClass = resolveMarkForCell('deliveredRate', period, record).redText
                                ? 'dashboard-mark-text-danger'
                                : undefined;
                            return wrapWithAnnotation('deliveredRate', columnType, dateKey, record, (
                                <span className={textClass}>{formatPercentage(value)}</span>
                            ));
                        },
                        onCell: (record: TableDataRow) => ({
                            className: markCellClassName(resolveMarkForCell('deliveredRate', period, record)),
                        }),
                    }))
                    : []),
            ],
        });

        // 指标4：入库漏扫率（最左列为合计，后端多天分子之和/分母之和）
        cols.push({
            title: (
                <span>
                    入库漏扫率
                    <MetricTooltip content="领取包裹中，缺少到达提货仓扫描的比例值" />
                </span>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('inboundMissScan')}
                        </div>
                    ),
                    dataIndex: 'inboundMissScan_total',
                    key: 'inboundMissScan_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('inboundMissScanRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value, 'inboundMissScanRate')}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.inboundMissScan
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `inboundMissScan_${period}`,
                        key: `inboundMissScan_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            return wrapWithAnnotation('inboundMissScanRate', columnType, dateKey, record, (
                                <span>{formatPercentage(value, 'inboundMissScanRate')}</span>
                            ));
                        },
                    }))
                    : []),
            ],
        });

        // 指标5：12小时领取率（最左列为合计，后端多天分子之和/分母之和）
        cols.push({
            title: (
                <span>
                    12小时领取率
                    <MetricTooltip content={appendTargetValueDesc('到货包裹中，到货后12小时内领取的比例值。分母：到whs包裹量（与最终领取率一致）', markCtx, 'pickup12hRate')} />
                </span>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('pickup12h')}
                        </div>
                    ),
                    dataIndex: 'pickup12h_total',
                    key: 'pickup12h_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('pickup12hRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.pickup12h
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `pickup12h_${period}`,
                        key: `pickup12h_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            const textClass = resolveMarkForCell('pickup12hRate', period, record).redText
                                ? 'dashboard-mark-text-danger'
                                : undefined;
                            return wrapWithAnnotation('pickup12hRate', columnType, dateKey, record, (
                                <span className={textClass}>{formatPercentage(value)}</span>
                            ));
                        },
                        onCell: (record: TableDataRow) => ({
                            className: markCellClassName(resolveMarkForCell('pickup12hRate', period, record)),
                        }),
                    }))
                    : []),
            ],
        });

        // 指标6：领取后24小时妥投率（最左列为合计，后端多天分子之和/分母之和）
        cols.push({
            title: (
                <span>
                    领取后24小时妥投率
                    <MetricTooltip content={appendTargetValueDesc('领取包裹中，领取后24小时内妥投的比例值', markCtx, 'delivered24hRate')} />
                </span>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('delivered24h')}
                        </div>
                    ),
                    dataIndex: 'delivered24h_total',
                    key: 'delivered24h_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('delivered24hRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.delivered24h
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `delivered24h_${period}`,
                        key: `delivered24h_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            const textClass = resolveMarkForCell('delivered24hRate', period, record).redText
                                ? 'dashboard-mark-text-danger'
                                : undefined;
                            return wrapWithAnnotation('delivered24hRate', columnType, dateKey, record, (
                                <span className={textClass}>{formatPercentage(value)}</span>
                            ));
                        },
                        onCell: (record: TableDataRow) => ({
                            className: markCellClassName(resolveMarkForCell('delivered24hRate', period, record)),
                        }),
                    }))
                    : []),
            ],
        });

        // 指标6b：退件72小时未完结率（与「领取后24小时妥投率」同口径：有日期列 + 合计列取后端 total 行）
        cols.push({
            title: (
                <div className="text-center">
                    <div>
                        <span>退件72小时未完结率</span>
                        <MetricTooltip content="有退件开始时间的包裹中，退件开始后72小时内未完结退件流程的比例。" />
                    </div>
                </div>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('returnUnfinished72h')}
                        </div>
                    ),
                    dataIndex: 'return_unfinished_72h_total',
                    key: 'return_unfinished_72h_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('returnUnfinished72hRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.returnUnfinished72h
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `returnUnfinished72h_${period}`,
                        key: `returnUnfinished72h_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            return wrapWithAnnotation('returnUnfinished72hRate', columnType, dateKey, record, (
                                <span>{formatPercentage(value)}</span>
                            ));
                        },
                    }))
                    : []),
            ],
        });

        // 指标6c：返仓24小时未处理率（与「领取后24小时妥投率」同口径：有日期列 + 合计列取后端 total 行）
        cols.push({
            title: (
                <div className="text-center">
                    <div>
                        <span>返仓24小时未处理率</span>
                        <MetricTooltip content="收到返仓下架处理指令的包裹中，指令发出后24小时内无司机领取动作（dscan+插单）的比例。" />
                    </div>
                </div>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('returnWarehouseNoPickup24h')}
                        </div>
                    ),
                    dataIndex: 'returnWarehouseNoPickup24h_total',
                    key: 'returnWarehouseNoPickup24h_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('returnWarehouseNoPickup24hRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.returnWarehouseNoPickup24h
                    ? timePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `returnWarehouseNoPickup24h_${period}`,
                        key: `returnWarehouseNoPickup24h_${period}`,
                        width: 120,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            return wrapWithAnnotation('returnWarehouseNoPickup24hRate', columnType, dateKey, record, (
                                <span>{formatPercentage(value)}</span>
                            ));
                        },
                    }))
                    : []),
            ],
        });

        // 指标7：最终未妥投包裹率（剔除最近3个自然日），按原因拆分为多个子列，每个原因都有日期子列
        const ndrReasons = NDR_REASONS;

        cols.push({
            title: (
                <div className="text-center">
                    <div>
                        <span>最终未妥投包裹率</span>
                        <MetricTooltip content={appendTargetValueDesc('未妥投包裹数 / 到货包裹数', markCtx, 'undeliveredRate')} />
                    </div>
                    <div className="text-xs whitespace-normal">（剔除最近3个自然日）</div>
                </div>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('undeliveredRateDates')}
                            {getToggleSubColumnsNode('undeliveredRate')}
                        </div>
                    ),
                    dataIndex: 'undelivered_rate',
                    key: 'undelivered_rate',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('undeliveredRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                ...(dateColumnsExpanded.undeliveredRateDates
                    ? ndrTimePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `undelivered_${period}`,
                        key: `undelivered_${period}`,
                        width: 100,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => {
                            const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                            const textClass = resolveMarkForCell('undeliveredRate', period, record).redText
                                ? 'dashboard-mark-text-danger'
                                : undefined;
                            return wrapWithAnnotation('undeliveredRate', columnType, dateKey, record, (
                                <span className={textClass}>{formatPercentage(value)}</span>
                            ));
                        },
                        onCell: (record: TableDataRow) => ({
                            className: markCellClassName(resolveMarkForCell('undeliveredRate', period, record)),
                        }),
                    }))
                    : []),
                ...(dateColumnsExpanded.undeliveredRate
                    ? ndrReasons.map(reason => ({
                        title: reason.tooltip ? (
                            <span>
                                {reason.label}
                                <MetricTooltip content={appendTargetValueDesc(reason.tooltip, markCtx, reason.markField ?? '')} />
                            </span>
                        ) : reason.label,
                        children: [
                            {
                                title: (
                                    <div className="flex items-center justify-end gap-1">
                                        <span>合计</span>
                                        {getToggleDateColumnsNode(`undeliveredRate_${reason.key}` as keyof typeof dateColumnsExpanded)}
                                    </div>
                                ),
                                dataIndex: reason.dataKey,
                                key: reason.dataKey,
                                width: 120,
                                align: 'right' as const,
                                render: (value: any, record: TableDataRow) =>
                                    wrapWithAnnotation(`undeliveredRate_${reason.key}`, 'AVG', annotationRangeDateKey, record, formatPercentage(value)),
                            },
                            ...(dateColumnsExpanded[`undeliveredRate_${reason.key}` as keyof typeof dateColumnsExpanded]
                                ? ndrTimePeriods.map(period => ({
                                    title: period.includes('~') ? (
                                        <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                            {period.replace('~', '~\n')}
                                        </div>
                                    ) : period,
                                    dataIndex: `${reason.rateKey}${period}`,
                                    key: `${reason.rateKey}${period}`,
                                    width: 100,
                                    align: 'right' as const,
                                    render: (value: any, record: TableDataRow) => {
                                        const { columnType, dateKey } = computeDeliveryAnnotationKey(period, timeRange);
                                        // 5 个 NDR 拆分原因中只有「未领取」参与标记
                                        const textClass =
                                            reason.key === 'unpickup' &&
                                            resolveMarkForCell(UNPICKUP_MARK_FIELD, period, record).redText
                                                ? 'dashboard-mark-text-danger'
                                                : undefined;
                                        return wrapWithAnnotation(`undeliveredRate_${reason.key}`, columnType, dateKey, record, (
                                            <span className={textClass}>{formatPercentage(value)}</span>
                                        ));
                                    },
                                    onCell: (record: TableDataRow) => ({
                                        className: reason.key === 'unpickup'
                                            ? markCellClassName(resolveMarkForCell(UNPICKUP_MARK_FIELD, period, record))
                                            : '',
                                    }),
                                }))
                                : []),
                        ],
                    }))
                    : []),
            ],
        });

        // 指标7-2：最终未妥投包裹数（带子列，对应上面的各原因数量，首列为合计，可折叠子列）
        cols.push({
            title: (
                <div className="text-center">
                    <div>
                        <span>最终未妥投包裹数</span>
                        <MetricTooltip content="到货且领取包裹中，截止目前未妥投的包裹数" />
                    </div>
                    <div className="text-xs whitespace-normal">（剔除最近3个自然日）</div>
                </div>
            ),
            children: [
                {
                    title: (
                        <div className="flex items-center justify-end gap-1">
                            <span>合计</span>
                            {getToggleDateColumnsNode('undeliveredCountDates')}
                            {getToggleSubColumnsNode('undeliveredCount')}
                        </div>
                    ),
                    dataIndex: 'undelivered_total',
                    key: 'undelivered_total',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) =>
                        wrapWithAnnotation('undeliveredCount', 'AVG', annotationRangeDateKey, record, formatNumber(value)),
                },
                ...(dateColumnsExpanded.undeliveredCountDates
                    ? ndrTimePeriods.map(period => ({
                        title: period.includes('~') ? (
                            <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                {period.replace('~', '~\n')}
                            </div>
                        ) : period,
                        dataIndex: `undeliveredCount_${period}`,
                        key: `undeliveredCount_${period}`,
                        width: 100,
                        align: 'right' as const,
                        render: (value: any, record: TableDataRow) => formatNumber(value),
                    }))
                    : []),
                ...(dateColumnsExpanded.undeliveredCount
                    ? ndrReasons.map(reason => ({
                        title: reason.label,
                        children: [
                            {
                                title: (
                                    <div className="flex items-center justify-end gap-1">
                                        <span>合计</span>
                                        {getToggleDateColumnsNode(`undeliveredCount_${reason.key}` as keyof typeof dateColumnsExpanded)}
                                    </div>
                                ),
                                dataIndex: reason.countTotalKey,
                                key: `count_${reason.key}`,
                                width: 120,
                                align: 'right' as const,
                                render: (value: any, record: TableDataRow) =>
                                    wrapWithAnnotation(`undeliveredCount_${reason.key}`, 'AVG', annotationRangeDateKey, record, formatNumber(value)),
                            },
                            ...(dateColumnsExpanded[`undeliveredCount_${reason.key}` as keyof typeof dateColumnsExpanded]
                                ? ndrTimePeriods.map(period => ({
                                    title: period.includes('~') ? (
                                        <div style={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                                            {period.replace('~', '~\n')}
                                        </div>
                                    ) : period,
                                    dataIndex: `${reason.countKey}${period}`,
                                    key: `count_${reason.countKey}${period}`,
                                    width: 100,
                                    align: 'right' as const,
                                    render: (value: any, record: TableDataRow) => formatNumber(value),
                                }))
                                : []),
                        ],
                    }))
                    : []),
            ],
        });

        // 指标8：72小时断更率（剔除最近3个自然日）
        cols.push({
            title: (
                <div className="text-center">
                    <div>72小时断更率</div>
                    <div className="text-xs whitespace-normal">（剔除最近3个自然日）</div>
                </div>
            ),
            children: [
                {
                    title: (
                        <span>
                            断更率
                            <MetricTooltip content="72h断更包裹数 / 到货包裹数" />
                        </span>
                    ),
                    dataIndex: 'no_update_rate',
                    key: 'no_update_rate',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('noUpdate72hRate', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatPercentage(value)}</span>
                        ));
                    },
                },
                {
                    title: (
                        <span>
                            断更包裹数
                            <MetricTooltip content="到货包裹中，截止目前未妥投、未完成退货、未丢失且最后一次实体轨迹更新距今超过72小时的包裹数" />
                        </span>
                    ),
                    dataIndex: 'no_update_count',
                    key: 'no_update_count',
                    width: 120,
                    align: 'right' as const,
                    render: (value: any, record: TableDataRow) => {
                        return wrapWithAnnotation('noUpdate72hCount', 'AVG', annotationRangeDateKey, record, (
                            <span>{formatNumber(value)}</span>
                        ));
                    },
                },
            ],
        });

        return cols;
    }, [timePeriods, markCtx, resolveMarkForCell, hubNameSortOrder, totalArrivedSortOrder, dateColumnsExpanded, timeRange, annotationsByKey, createDashboardAnnotation, updateDashboardAnnotation, deleteDashboardAnnotation]);

    /**
     * 构建全部表格行（HUB + WHS + DSP 三层），不受展开状态影响。
     *
     * 行的构建与可见性在此分离：过去 tableData 依赖 expandedHubs，展开一个 WHS 就要重建
     * 所有行对象，即使数据未变也会让 antd 重渲染整表数百列的单元格。现在 allRows 引用稳定，
     * 展开只做数组过滤，未变的行能被复用，代价降到「只渲染新增的那几行」。
     */
    const allRows = useMemo(() => {
        if (!dashboardData?.queryDeliveryDashboard || !summaryData?.queryDeliveryDashboardSummary?.items) {
            return [];
        }

        const dashboardItems = dashboardData.queryDeliveryDashboard;
        const summaryItems = summaryData.queryDeliveryDashboardSummary.items;

        /**
         * (时间周期, hub, warehouse, dsp) → 数据行 的索引。
         *
         * 每行每列都要按这四个维度定位数据，逐格 find 会退化成
         * 「行数 × 日期数 × 数据量」的线性扫描，展开 HUB 后卡顿明显。
         * 同 key 保留首条，与原先 find 取首个匹配的语义一致。
         */
        const itemKeyOf = (period: string, hubId: unknown, warehouseId: unknown, dspId: unknown) =>
            `${period}|${Number(hubId)}|${Number(warehouseId)}|${Number(dspId)}`;
        const itemIndex = new Map<string, DeliveryDashboardItem>();
        dashboardItems.forEach(item => {
            const key = itemKeyOf(item.timePeriod, item.hubId, item.warehouseId, item.dspId);
            if (!itemIndex.has(key)) itemIndex.set(key, item);
        });

        /** (hub, warehouse) → 汇总行 */
        const summaryIndex = new Map<string, (typeof summaryItems)[number]>();
        summaryItems.forEach(item => {
            const key = `${Number(item.hubId)}|${Number(item.warehouseId)}`;
            if (!summaryIndex.has(key)) summaryIndex.set(key, item);
        });

        const rows: TableDataRow[] = [];

        // 遍历所有Hub（包括hubId=0的全美）
        columnGroups.forEach(group => {
            const hubKey = `hub_${group.hubId}`;

            // Hub行显示名称：hubId=0显示"全美"，其他显示 sortingCode 经 formatHubDisplayLabel 转换（LA→LAX）
            const displayName = group.hubId == 0 ? '全美' : (formatHubDisplayLabel(group.sortingCode) || `Hub-${group.hubId}`);

            const hubRow: TableDataRow = {
                key: hubKey,
                metricName: displayName,
                isExpandable: group.warehouses.length > 0,
                isDetail: false,
                hubId: group.hubId,
                warehouseId: 0,
                sortingCode: group.sortingCode,
                sortKey: displayName,
                sortRank: 0,
                ancestorKeys: [],
            };

            // 填充前6个指标的数据（有时间周期）
            timePeriods.forEach(period => {
                // hub 行取 dspId=-1（全 hub 汇总，不涉及 DSP）
                const hubItem = itemIndex.get(itemKeyOf(period, group.hubId, 0, -1));

                hubRow[`arrived_${period}`] = hubItem?.arrivedCount || 0;
                hubRow[`pickup_${period}`] = hubItem?.pickupRate || '-';
                hubRow[`delivered_${period}`] = hubItem?.deliveredRate || '-';
                hubRow[`inboundMissScan_${period}`] = hubItem?.inboundMissScanRate || '-';
                hubRow[`pickup12h_${period}`] = hubItem?.pickup12hRate || '-';
                hubRow[`delivered24h_${period}`] = hubItem?.delivered24hRate || '-';
                hubRow[`returnUnfinished72h_${period}`] = hubItem?.returnUnfinished72hRate || '-';
                hubRow[`returnWarehouseNoPickup24h_${period}`] = hubItem?.returnWarehouseNoPickup24hRate || '-';
                hubRow[`undelivered_${period}`] = hubItem?.undeliveredRate || '-';
                hubRow[`undeliveredCount_${period}`] = hubItem?.undeliveredCount || 0;

                // 填充各原因的日期数据（用于拆分到每个原因的日期列）
                hubRow[`unpickup_count_${period}`] = hubItem?.unpickupCount || 0;
                hubRow[`delivering_count_${period}`] = hubItem?.deliveringCount || 0;
                hubRow[`deliveryFailed_count_${period}`] = hubItem?.deliveryFailedCount || 0;
                hubRow[`return_count_${period}`] = hubItem?.returnCount || 0;
                hubRow[`lostDamaged_count_${period}`] = hubItem?.lostDamagedCount || 0;

                // 计算各原因在该期间的比率（基数为该期间的到货数）
                const periodArrivedCount = Number(hubRow[`arrived_${period}`]) || 0;
                const toRateStr = (count: number) =>
                    periodArrivedCount > 0 && count > 0
                        ? `${((count / periodArrivedCount) * 100).toFixed(2)}%`
                        : '0.00%';
                hubRow[`unpickup_rate_${period}`] = toRateStr(Number(hubRow[`unpickup_count_${period}`]) || 0);
                hubRow[`delivering_rate_${period}`] = toRateStr(Number(hubRow[`delivering_count_${period}`]) || 0);
                hubRow[`deliveryFailed_rate_${period}`] = toRateStr(Number(hubRow[`deliveryFailed_count_${period}`]) || 0);
                hubRow[`return_rate_${period}`] = toRateStr(Number(hubRow[`return_count_${period}`]) || 0);
                hubRow[`lostDamaged_rate_${period}`] = toRateStr(Number(hubRow[`lostDamaged_count_${period}`]) || 0);
            });
            hubRow['arrived_total'] = timePeriods.reduce((sum, period) => sum + (Number(hubRow[`arrived_${period}`]) || 0), 0);

            // 率类合计列：取后端「合计」行（多天分子之和/分母之和），不做前端简单平均
            const totalItem = itemIndex.get(itemKeyOf(TIME_PERIOD_TOTAL, group.hubId, 0, -1));
            if (totalItem) {
                hubRow['pickup_total'] = totalItem.pickupRate ?? '-';
                hubRow['delivered_total'] = totalItem.deliveredRate ?? '-';
                hubRow['inboundMissScan_total'] = totalItem.inboundMissScanRate ?? '-';
                hubRow['pickup12h_total'] = totalItem.pickup12hRate ?? '-';
                hubRow['delivered24h_total'] = totalItem.delivered24hRate ?? '-';
                hubRow['return_unfinished_72h_total'] = totalItem.returnUnfinished72hRate ?? '-';
                hubRow['returnWarehouseNoPickup24h_total'] = totalItem.returnWarehouseNoPickup24hRate ?? '-';
            } else {
                hubRow['pickup_total'] = '-';
                hubRow['delivered_total'] = '-';
                hubRow['inboundMissScan_total'] = '-';
                hubRow['pickup12h_total'] = '-';
                hubRow['delivered24h_total'] = '-';
                hubRow['return_unfinished_72h_total'] = '-';
                hubRow['returnWarehouseNoPickup24h_total'] = '-';
            }

            // 填充后2个指标的数据（汇总数据）
            const hubSummary = summaryIndex.get(`${Number(group.hubId)}|0`);

            hubRow['undelivered_rate'] = hubSummary?.undeliveredRate || '-';
            hubRow['undelivered_total'] = hubSummary?.undeliveredCount || 0;
            hubRow['unpickup_count'] = hubSummary?.unpickupCount || 0;
            hubRow['undelivered_delivering'] = hubSummary?.deliveringCount || 0;
            hubRow['undelivered_delivery_failed'] = hubSummary?.deliveryFailedCount || 0;
            hubRow['undelivered_return'] = hubSummary?.returnCount || 0;
            hubRow['undelivered_lost_damaged'] = hubSummary?.lostDamagedCount || 0;
            hubRow['no_update_rate'] = hubSummary?.noUpdate72hRate || '-';
            hubRow['no_update_count'] = hubSummary?.noUpdate72hCount || 0;
            // 新增两列口径与「领取后24小时妥投率」一致：来自 queryDeliveryDashboard（包含 total 行），不从 summary 取

            // 基于汇总数据计算各原因的未妥投率（剔除最近3个自然日）：原因包裹数 / 到货包裹数
            const hubArrivedBase = Number(hubSummary?.arrivedCount ?? 0);
            if (hubArrivedBase > 0 && hubRow['undelivered_total'] > 0) {
                const toPercentStr = (count: number) =>
                    count > 0 ? `${((count / hubArrivedBase) * 100).toFixed(2)}%` : '0.00%';
                hubRow['undelivered_unpickup_rate'] = toPercentStr(Number(hubRow['unpickup_count']) || 0);
                hubRow['undelivered_delivering_rate'] = toPercentStr(Number(hubRow['undelivered_delivering']) || 0);
                hubRow['undelivered_delivery_failed_rate'] = toPercentStr(Number(hubRow['undelivered_delivery_failed']) || 0);
                hubRow['undelivered_return_rate'] = toPercentStr(Number(hubRow['undelivered_return']) || 0);
                hubRow['undelivered_lost_damaged_rate'] = toPercentStr(Number(hubRow['undelivered_lost_damaged']) || 0);
            }
            
            rows.push(hubRow);

            // warehouse 明细行一律构建，是否显示由 visibleRows 按展开态过滤
            {
                group.warehouses.forEach((warehouse, wi) => {
                    const warehouseDisplayName = warehouse.sortingCode || `W-${warehouse.warehouseId}`;

                    const warehouseKey = `warehouse_${group.hubId}_${warehouse.warehouseId}`;
                    const hasDsps = (warehouse.dsps?.length ?? 0) > 0;

                    const warehouseRow: TableDataRow = {
                        key: warehouseKey,
                        metricName: warehouseDisplayName,
                        isExpandable: hasDsps,
                        isDetail: true,
                        hubId: group.hubId,
                        warehouseId: warehouse.warehouseId,
                        sortingCode: warehouse.sortingCode,
                        sortKey: displayName,
                        sortRank: 1 + wi,
                        ancestorKeys: [hubKey],
                    };

                    // 填充前6个指标的数据（有时间周期）
                    timePeriods.forEach(period => {
                        const warehouseItem = itemIndex.get(
                            itemKeyOf(period, group.hubId, warehouse.warehouseId, -1)
                        );

                        warehouseRow[`arrived_${period}`] = warehouseItem?.arrivedCount || 0;
                        warehouseRow[`pickup_${period}`] = warehouseItem?.pickupRate || '-';
                        warehouseRow[`delivered_${period}`] = warehouseItem?.deliveredRate || '-';
                        warehouseRow[`inboundMissScan_${period}`] = warehouseItem?.inboundMissScanRate || '-';
                        warehouseRow[`pickup12h_${period}`] = warehouseItem?.pickup12hRate || '-';
                        warehouseRow[`delivered24h_${period}`] = warehouseItem?.delivered24hRate || '-';
                        warehouseRow[`returnUnfinished72h_${period}`] = warehouseItem?.returnUnfinished72hRate || '-';
                        warehouseRow[`returnWarehouseNoPickup24h_${period}`] =
                            warehouseItem?.returnWarehouseNoPickup24hRate || '-';
                        warehouseRow[`undelivered_${period}`] = warehouseItem?.undeliveredRate || '-';
                        warehouseRow[`undeliveredCount_${period}`] = warehouseItem?.undeliveredCount || 0;

                        // 填充各原因的日期数据
                        warehouseRow[`unpickup_count_${period}`] = warehouseItem?.unpickupCount || 0;
                        warehouseRow[`delivering_count_${period}`] = warehouseItem?.deliveringCount || 0;
                        warehouseRow[`deliveryFailed_count_${period}`] = warehouseItem?.deliveryFailedCount || 0;
                        warehouseRow[`return_count_${period}`] = warehouseItem?.returnCount || 0;
                        warehouseRow[`lostDamaged_count_${period}`] = warehouseItem?.lostDamagedCount || 0;

                        // 计算各原因在该期间的比率（基数为该期间的到货数）
                        const periodArrivedCount = Number(warehouseRow[`arrived_${period}`]) || 0;
                        const toRateStr = (count: number) =>
                            periodArrivedCount > 0 && count > 0
                                ? `${((count / periodArrivedCount) * 100).toFixed(2)}%`
                                : '0.00%';
                        warehouseRow[`unpickup_rate_${period}`] = toRateStr(Number(warehouseRow[`unpickup_count_${period}`]) || 0);
                        warehouseRow[`delivering_rate_${period}`] = toRateStr(Number(warehouseRow[`delivering_count_${period}`]) || 0);
                        warehouseRow[`deliveryFailed_rate_${period}`] = toRateStr(Number(warehouseRow[`deliveryFailed_count_${period}`]) || 0);
                        warehouseRow[`return_rate_${period}`] = toRateStr(Number(warehouseRow[`return_count_${period}`]) || 0);
                        warehouseRow[`lostDamaged_rate_${period}`] = toRateStr(Number(warehouseRow[`lostDamaged_count_${period}`]) || 0);
                    });
                    warehouseRow['arrived_total'] = timePeriods.reduce((sum, period) => sum + (Number(warehouseRow[`arrived_${period}`]) || 0), 0);

                    // 率类合计列：后端「合计」行
                    const whTotalItem = itemIndex.get(
                        itemKeyOf(TIME_PERIOD_TOTAL, group.hubId, warehouse.warehouseId, -1)
                    );
                    if (whTotalItem) {
                        warehouseRow['pickup_total'] = whTotalItem.pickupRate ?? '-';
                        warehouseRow['delivered_total'] = whTotalItem.deliveredRate ?? '-';
                        warehouseRow['inboundMissScan_total'] = whTotalItem.inboundMissScanRate ?? '-';
                        warehouseRow['pickup12h_total'] = whTotalItem.pickup12hRate ?? '-';
                        warehouseRow['delivered24h_total'] = whTotalItem.delivered24hRate ?? '-';
                        warehouseRow['return_unfinished_72h_total'] = whTotalItem.returnUnfinished72hRate ?? '-';
                        warehouseRow['returnWarehouseNoPickup24h_total'] =
                            whTotalItem.returnWarehouseNoPickup24hRate ?? '-';
                    } else {
                        warehouseRow['pickup_total'] = '-';
                        warehouseRow['delivered_total'] = '-';
                        warehouseRow['inboundMissScan_total'] = '-';
                        warehouseRow['pickup12h_total'] = '-';
                        warehouseRow['delivered24h_total'] = '-';
                        warehouseRow['return_unfinished_72h_total'] = '-';
                        warehouseRow['returnWarehouseNoPickup24h_total'] = '-';
                    }

                    // 填充后2个指标的数据（汇总数据）
                    const warehouseSummary = summaryIndex.get(
                        `${Number(group.hubId)}|${Number(warehouse.warehouseId)}`
                    );
                    warehouseRow['undelivered_rate'] = warehouseSummary?.undeliveredRate || '-';
                    warehouseRow['undelivered_total'] = warehouseSummary?.undeliveredCount || 0;
                    warehouseRow['unpickup_count'] = warehouseSummary?.unpickupCount || 0;
                    warehouseRow['undelivered_delivering'] = warehouseSummary?.deliveringCount || 0;
                    warehouseRow['undelivered_delivery_failed'] = warehouseSummary?.deliveryFailedCount || 0;
                    warehouseRow['undelivered_return'] = warehouseSummary?.returnCount || 0;
                    warehouseRow['undelivered_lost_damaged'] = warehouseSummary?.lostDamagedCount || 0;
                    warehouseRow['no_update_rate'] = warehouseSummary?.noUpdate72hRate || '-';
                    warehouseRow['no_update_count'] = warehouseSummary?.noUpdate72hCount || 0;
                    // 新增两列口径与「领取后24小时妥投率」一致：来自 queryDeliveryDashboard（包含 total 行），不从 summary 取

                    const warehouseArrivedBase = Number(warehouseSummary?.arrivedCount ?? 0);
                    if (warehouseArrivedBase > 0 && warehouseRow['undelivered_total'] > 0) {
                        const toPercentStr = (count: number) =>
                            count > 0 ? `${((count / warehouseArrivedBase) * 100).toFixed(2)}%` : '0.00%';
                        warehouseRow['undelivered_unpickup_rate'] = toPercentStr(Number(warehouseRow['unpickup_count']) || 0);
                        warehouseRow['undelivered_delivering_rate'] = toPercentStr(Number(warehouseRow['undelivered_delivering']) || 0);
                        warehouseRow['undelivered_delivery_failed_rate'] = toPercentStr(Number(warehouseRow['undelivered_delivery_failed']) || 0);
                        warehouseRow['undelivered_return_rate'] = toPercentStr(Number(warehouseRow['undelivered_return']) || 0);
                        warehouseRow['undelivered_lost_damaged_rate'] = toPercentStr(Number(warehouseRow['undelivered_lost_damaged']) || 0);
                    }
                    
                    rows.push(warehouseRow);

                    // DSP 明细行一律构建（仅5个核心指标：到货、领取率、妥投率、12h领取率、24h妥投率）
                    if (hasDsps) {
                        warehouse.dsps!.forEach(dsp => {
                            const dspDisplayName = dsp.dspId === 0 ? '未分配' : (dsp.dspName || `DSP-${dsp.dspId}`);

                            const dspRow: TableDataRow = {
                                key: `dsp_${group.hubId}_${warehouse.warehouseId}_${dsp.dspId}`,
                                metricName: dspDisplayName,
                                isExpandable: false,
                                isDetail: true,
                                hubId: group.hubId,
                                warehouseId: warehouse.warehouseId,
                                dspId: dsp.dspId,
                                sortKey: displayName,
                                sortRank: 1 + wi,
                                ancestorKeys: [hubKey, warehouseKey],
                            };

                            timePeriods.forEach(period => {
                                const dspItem = itemIndex.get(
                                    itemKeyOf(period, group.hubId, warehouse.warehouseId, dsp.dspId)
                                );

                                dspRow[`arrived_${period}`] = dspItem?.arrivedCount || 0;
                                dspRow[`pickup_${period}`] = dspItem?.pickupRate || '-';
                                dspRow[`delivered_${period}`] = dspItem?.deliveredRate || '-';
                                dspRow[`pickup12h_${period}`] = dspItem?.pickup12hRate || '-';
                                dspRow[`delivered24h_${period}`] = dspItem?.delivered24hRate || '-';
                            });
                            dspRow['arrived_total'] = timePeriods.reduce((sum, period) => sum + (Number(dspRow[`arrived_${period}`]) || 0), 0);

                            // 率类合计列：后端「合计」行
                            const dspTotalItem = itemIndex.get(
                                itemKeyOf(TIME_PERIOD_TOTAL, group.hubId, warehouse.warehouseId, dsp.dspId)
                            );
                            if (dspTotalItem) {
                                dspRow['pickup_total'] = dspTotalItem.pickupRate ?? '-';
                                dspRow['delivered_total'] = dspTotalItem.deliveredRate ?? '-';
                                dspRow['pickup12h_total'] = dspTotalItem.pickup12hRate ?? '-';
                                dspRow['delivered24h_total'] = dspTotalItem.delivered24hRate ?? '-';
                            } else {
                                dspRow['pickup_total'] = '-';
                                dspRow['delivered_total'] = '-';
                                dspRow['pickup12h_total'] = '-';
                                dspRow['delivered24h_total'] = '-';
                            }

                            rows.push(dspRow);
                        });
                    }
                });
            }
        });

        return rows;
    }, [dashboardData, summaryData, timePeriods, columnGroups]);

    /**
     * 数据层（列定义 + 全部行）的延后值。
     *
     * 只把「来自查询的数据」降为低优先级：切换筛选时 loading 的变化先单独提交，
     * 转圈立即消失，数百列的新表在后台渲染完再提交。
     * 展开态刻意不在其中——展开需要即时反馈，见下方 visibleRows。
     * columns 与 allRows 打包同步 defer，避免列与数据错配。
     */
    const dataView = useMemo(() => ({ columns, allRows }), [columns, allRows]);
    const deferredDataView = useDeferredValue(dataView);
    /** 延后值尚未追上最新值，说明新数据正在后台渲染 */
    const isTablePending = deferredDataView !== dataView;

    /**
     * 可见行 = 全部祖先都处于展开态的行。
     *
     * 用最新的 expandedHubs（不经 defer），所以点击展开立即生效；
     * 而 allRows 引用不变，未变的行对象能被复用，只需渲染新增的那几行。
     */
    const visibleRows = useMemo(
        () => deferredDataView.allRows.filter(row => row.ancestorKeys.every(key => expandedHubs.has(key))),
        [deferredDataView, expandedHubs]
    );

    // 按 hub 组名称或按到货包裹数合计排序（前端排序）。全美行固定在最前不参与排序；其余按「组」排序，每组 = 一个 hub 行 + 其下所有 warehouse 行
    const sortedTableData = useMemo(() => {
        if (!hubNameSortOrder && !totalArrivedSortOrder) return visibleRows;
        const nationwideRows = visibleRows.filter(row => (row.sortKey ?? '') === '全美');
        const otherRows = visibleRows.filter(row => (row.sortKey ?? '') !== '全美');
        const groups: TableDataRow[][] = [];
        for (const row of otherRows) {
            if ((row.sortRank ?? 0) === 0) groups.push([row]);
            else groups[groups.length - 1]?.push(row);
        }
        // 将 warehouse 行与其下属的 DSP 明细行捆绑为一个簇，排序时整簇随 warehouse 行移动，簇内 DSP 顺序不变
        const clusterByWarehouse = (rows: TableDataRow[]): TableDataRow[][] => {
            const clusters: TableDataRow[][] = [];
            rows.forEach(row => {
                if (row.dspId == null) clusters.push([row]);
                else clusters[clusters.length - 1]?.push(row);
            });
            return clusters;
        };
        if (totalArrivedSortOrder) {
            const hubTotal = (g: TableDataRow[]) => Number(g[0]?.arrived_total) || 0;
            groups.sort((ga, gb) => {
                const diff = hubTotal(ga) - hubTotal(gb);
                return totalArrivedSortOrder === 'descend' ? -diff : diff;
            });
            // 组内按合计排序：hub 行保持第一位，其下 warehouse 簇按各自 arrived_total 排序
            const dir = totalArrivedSortOrder === 'descend' ? -1 : 1;
            groups.forEach(g => {
                if (g.length <= 1) return;
                const [hubRow, ...warehouseRows] = g;
                const clusters = clusterByWarehouse(warehouseRows);
                clusters.sort((ca, cb) => {
                    const va = Number(ca[0]?.arrived_total) || 0;
                    const vb = Number(cb[0]?.arrived_total) || 0;
                    return va === vb ? 0 : (va < vb ? -dir : dir);
                });
                g.length = 0;
                g.push(hubRow, ...clusters.flat());
            });
        } else {
            const cmp = (a: string, b: string) => a.localeCompare(b, 'zh');
            groups.sort((ga, gb) => {
                const c = cmp(ga[0]?.sortKey ?? '', gb[0]?.sortKey ?? '');
                return hubNameSortOrder === 'descend' ? -c : c;
            });
            // 按指标列排序时，组内 warehouse 簇也按名称同向排序（降序时 W-43 在 W-41 前）
            const whsCmp = (a: string, b: string) => a.localeCompare(b, 'zh');
            groups.forEach(g => {
                if (g.length <= 1) return;
                const [hubRow, ...warehouseRows] = g;
                const clusters = clusterByWarehouse(warehouseRows);
                clusters.sort((ca, cb) => {
                    const ma = ca[0]?.metricName ?? '';
                    const mb = cb[0]?.metricName ?? '';
                    const c = whsCmp(ma, mb);
                    return hubNameSortOrder === 'descend' ? -c : c;
                });
                g.length = 0;
                g.push(hubRow, ...clusters.flat());
            });
        }
        const otherSorted = groups.flat();
        return [...nationwideRows, ...otherSorted];
    }, [visibleRows, hubNameSortOrder, totalArrivedSortOrder]);

    // 拆成固定区（全美行）与滚动区，使全美行不参与滚动
    const nationwideOnlyData = useMemo(
        () => sortedTableData.filter(row => row.key === 'hub_0'),
        [sortedTableData]
    );
    const restTableData = useMemo(
        () => sortedTableData.filter(row => row.key !== 'hub_0'),
        [sortedTableData]
    );

    // 同步两个 Table 的横向滚动，使表头表体一起滚动且第一列 fixed 生效
    const tableHeadRef = useRef<HTMLDivElement>(null);
    const tableBodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (nationwideOnlyData.length === 0 || restTableData.length === 0) return;
        const headWrap = tableHeadRef.current;
        const bodyWrap = tableBodyRef.current;
        const getScrollEl = (el: Element | null) =>
            (el?.querySelector('.ant-table-body') || el?.querySelector('.ant-table-content')) as HTMLElement | null;
        const headBody = getScrollEl(headWrap ?? null);
        const headHeader = headWrap?.querySelector('.ant-table-header') as HTMLElement | null;
        const bodyBody = getScrollEl(bodyWrap ?? null);
        if (!headBody || !bodyBody) return;

        const syncToBody = (scrollLeft: number) => {
            if (headBody.scrollLeft !== scrollLeft) headBody.scrollLeft = scrollLeft;
            if (headHeader && headHeader.scrollLeft !== scrollLeft) headHeader.scrollLeft = scrollLeft;
            if (bodyBody.scrollLeft !== scrollLeft) bodyBody.scrollLeft = scrollLeft;
        };

        const onHeadScroll = () => syncToBody(headBody.scrollLeft);
        const onBodyScroll = () => syncToBody(bodyBody.scrollLeft);

        headBody.addEventListener('scroll', onHeadScroll);
        if (headHeader) headHeader.addEventListener('scroll', onHeadScroll);
        bodyBody.addEventListener('scroll', onBodyScroll);

        return () => {
            headBody.removeEventListener('scroll', onHeadScroll);
            headHeader?.removeEventListener('scroll', onHeadScroll);
            bodyBody.removeEventListener('scroll', onBodyScroll);
        };
    }, [nationwideOnlyData.length, restTableData.length]);

    const [activeTab, setActiveTab] = useState<string>('overview');
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [generateDashboardTableExport] = useMutation<
        GenerateDashboardTableExportResponse,
        GenerateDashboardTableExportVariables
    >(GENERATE_DASHBOARD_TABLE_EXPORT);

    const handleDownloadDelivery = useCallback(async () => {
        if (!timeRange) {
            message.warning('请先选择时间范围');
            return;
        }
        if (!sortedTableData || sortedTableData.length === 0) {
            message.warning('当前条件下无尾程数据可导出');
            return;
        }
        if (exportLoading) {
            message.warning('导出任务生成中，请稍候');
            return;
        }

        const periodCols = timePeriods ?? [];
        // NDR 指标剔除近3自然日，与页面 ndrTimePeriods 保持一致
        const ndrPeriodCols = periodCols.filter((p) => !shouldHidePeriodForNdr(p, timeRange.queryType));

        const columns: { title: string; dataIndex: string }[] = [
            { title: '指标', dataIndex: 'metricName' },
        ];

        columns.push(
            { title: '到货-合计', dataIndex: 'arrived_total' },
            ...periodCols.map((p) => ({
                title: `到货-${p}`,
                dataIndex: `arrived_${p}`,
            })),
        );

        columns.push(
            { title: '领取率-合计', dataIndex: 'pickup_total' },
            ...periodCols.map((p) => ({
                title: `领取率-${p}`,
                dataIndex: `pickup_${p}`,
            })),
        );

        columns.push(
            { title: '领取妥投率-合计', dataIndex: 'delivered_total' },
            ...periodCols.map((p) => ({
                title: `领取妥投率-${p}`,
                dataIndex: `delivered_${p}`,
            })),
        );

        columns.push(
            { title: '入库漏扫率-合计', dataIndex: 'inboundMissScan_total' },
            ...periodCols.map((p) => ({
                title: `入库漏扫率-${p}`,
                dataIndex: `inboundMissScan_${p}`,
            })),
        );

        columns.push(
            { title: '12h领取率-合计', dataIndex: 'pickup12h_total' },
            ...periodCols.map((p) => ({
                title: `12h领取率-${p}`,
                dataIndex: `pickup12h_${p}`,
            })),
        );

        columns.push(
            { title: '24h妥投率-合计', dataIndex: 'delivered24h_total' },
            ...periodCols.map((p) => ({
                title: `24h妥投率-${p}`,
                dataIndex: `delivered24h_${p}`,
            })),
        );

        columns.push(
            { title: '退件72h未完结率-合计', dataIndex: 'return_unfinished_72h_total' },
            ...periodCols.map((p) => ({
                title: `退件72h未完结率-${p}`,
                dataIndex: `returnUnfinished72h_${p}`,
            })),
        );

        columns.push(
            { title: '返仓24h未处理率-合计', dataIndex: 'returnWarehouseNoPickup24h_total' },
            ...periodCols.map((p) => ({
                title: `返仓24h未处理率-${p}`,
                dataIndex: `returnWarehouseNoPickup24h_${p}`,
            })),
        );

        columns.push(
            { title: '未妥投率-合计', dataIndex: 'undelivered_rate' },
            ...ndrPeriodCols.map((p) => ({
                title: `未妥投率-${p}`,
                dataIndex: `undelivered_${p}`,
            })),
            ...NDR_REASONS.flatMap((reason) => [
                { title: `未妥投率-${reason.label}`, dataIndex: reason.dataKey },
                ...ndrPeriodCols.map((p) => ({
                    title: `未妥投率-${reason.label}-${p}`,
                    dataIndex: `${reason.rateKey}${p}`,
                })),
            ]),
            { title: '未妥投包裹数-合计', dataIndex: 'undelivered_total' },
            ...ndrPeriodCols.map((p) => ({
                title: `未妥投包裹数-${p}`,
                dataIndex: `undeliveredCount_${p}`,
            })),
            ...NDR_REASONS.flatMap((reason) => [
                { title: `未妥投包裹数-${reason.label}`, dataIndex: reason.countTotalKey },
                ...ndrPeriodCols.map((p) => ({
                    title: `未妥投包裹数-${reason.label}-${p}`,
                    dataIndex: `${reason.countKey}${p}`,
                })),
            ]),
            { title: '72h无更新率', dataIndex: 'no_update_rate' },
            { title: '72h无更新总量', dataIndex: 'no_update_count' },
        );

        const headers = columns.map((c) => c.title);
        const mainRows = sortedTableData.map((row) =>
            columns.map((c) => dashboardTableExportCellString((row as Record<string, unknown>)[c.dataIndex]))
        );

        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const auditSummary = `delivery queryType=${timeRange.queryType} ${timeRange.startDate}..${timeRange.endDate} hubId=${selectedHubId}`;
                const response = await generateDashboardTableExport({
                    variables: {
                        input: {
                            fileNamePrefix: 'Delivery_Dashboard',
                            timezone: userTimezone,
                            snapshotSheetName: 'Sheet1',
                            mainSheetName: 'Sheet1',
                            snapshotRows: null,
                            mainColumnHeaders: headers,
                            mainRows,
                            auditSummary,
                        },
                    },
                });
                const fileName = response.data?.generateDashboardTableExport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error('导出失败，请稍后重试');
                }
            } catch (error: unknown) {
                showError(error);
            }
        });
    }, [
        timeRange,
        sortedTableData,
        timePeriods,
        selectedHubId,
        generateDashboardTableExport,
        exportLoading,
        runExportWithCooldown,
    ]);

    return (
        <div className="p-6 min-h-full flex flex-col">
            <Card
                title={
                    <span className="text-xl font-semibold">
                        尾程数据看板
                        <span className="text-sm font-normal ml-3">（时区说明: 日期均为美西时区{dataUpdatedAtText && `；数据更新于: ${dataUpdatedAtText}`}）</span>
                    </span>
                }
                bordered={false}
                className="flex-1"
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'overview',
                            label: '数据概况',
                            children: (
                                <>
                                    {/* 第一行：日期筛选（尾程数据不展示「今日实时」预设） */}
                                    <div className="mb-4">
                                        <TimeRangeFilter
                                            onChange={handleTimeRangeChange}
                                            maxDaysBack={365}
                                            dashboardTimezone={DASHBOARD_PACIFIC_TZ}
                                            presetKeysByQueryType={{
                                                [ScaleQueryType.DAILY]: ['yesterday', 'last3days', 'last7days', 'last15days', 'custom'],
                                            }}
                                        />
                                    </div>
                                    {/* 第二行：分拣仓名称 + 日期列展开/收起 + 下载按钮 */}
                                    <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-semibold text-gray-800 whitespace-nowrap">分拣仓名称</span>
                                            <Select
                                                style={{ width: 320 }}
                                                value={selectedHubId}
                                                onChange={handleHubChange}
                                                options={hubOptions}
                                                loading={hubsLoading}
                                                placeholder="选择分拣仓"
                                            />
                                        </div>
                                        <Button
                                            type="primary"
                                            onClick={() => void handleDownloadDelivery()}
                                            loading={exportLoading}
                                            disabled={exportLoading}
                                            className="w-auto min-w-24"
                                        >
                                            <span className="font-bold">
                                                {getExportButtonLabel()}
                                            </span>
                                        </Button>
                                    </div>

                                    {/* 数据表格：有全美时拆成上下两块，第一列 fixed 固定；两表横向滚动同步；全美行不显示横向滚动条 */}
                                    <style>{`
.delivery-dashboard-table-head-hide-scrollbar .ant-table-body,
.delivery-dashboard-table-head-hide-scrollbar .ant-table-content {
    scrollbar-width: none;
    -ms-overflow-style: none;
}
.delivery-dashboard-table-head-hide-scrollbar .ant-table-body::-webkit-scrollbar,
.delivery-dashboard-table-head-hide-scrollbar .ant-table-content::-webkit-scrollbar {
    display: none;
}
/* 修复固定列被遮挡问题：提高固定列z-index以覆盖滚动内容 */
.delivery-dashboard-table .ant-table-cell-fix-left {
    z-index: 2 !important;
}
.delivery-dashboard-table .ant-table-cell-fix-left::after {
    z-index: 2 !important;
}
.delivery-dashboard-table .ant-table-sticky > thead > tr > th {
    z-index: 3 !important;
}
                                    `}</style>
                                    <DashboardMarkLegend ctx={markCtx} metricLabels={DELIVERY_MARK_METRIC_LABELS} />
                                    <Spin spinning={loading}>
                                        {/* 表格用延后值渲染，未追上时明确提示，避免把上一次筛选的数据误读为当前结果 */}
                                        {isTablePending && (
                                            <div className="mb-2 text-xs text-gray-400">正在更新表格…</div>
                                        )}
                                        {nationwideOnlyData.length > 0 && restTableData.length > 0 ? (
                                            <div className="delivery-dashboard-table w-full">
                                                <div ref={tableHeadRef} className="delivery-dashboard-table-head-hide-scrollbar">
                                                    <Table
                                                        columns={deferredDataView.columns}
                                                        dataSource={nationwideOnlyData}
                                                        rowKey="key"
                                                        tableLayout="fixed"
                                                        pagination={false}
                                                        scroll={{ x: 'max-content' }}
                                                        sticky
                                                        bordered
                                                        size="middle"
                                                        className="delivery-dashboard-table"
                                                        style={{ marginBottom: 0 }}
                                                    />
                                                </div>
                                                <div ref={tableBodyRef}>
                                                    <Table
                                                        columns={deferredDataView.columns}
                                                        dataSource={restTableData}
                                                        rowKey="key"
                                                        showHeader={false}
                                                        tableLayout="fixed"
                                                        pagination={false}
                                                        scroll={{ x: 'max-content' }}
                                                        bordered
                                                        size="middle"
                                                        className="delivery-dashboard-table -mt-px"
                                                        rowClassName={(_, index) => (index === 0 ? '[&>td]:border-t-0' : '')}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <Table
                                                columns={deferredDataView.columns}
                                                dataSource={sortedTableData}
                                                rowKey="key"
                                                tableLayout="fixed"
                                                pagination={false}
                                                scroll={{ x: 'max-content' }}
                                                bordered
                                                size="middle"
                                                className="delivery-dashboard-table"
                                                sticky
                                            />
                                        )}
                                    </Spin>
                                </>
                            ),
                        },
                        {
                            key: 'ranking',
                            label: '车队排名',
                            children: <DspRankingPage />,
                        },
                    ]}
                />
            </Card>
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.lastMile}
            />
        </div>
    );
};

export default DeliveryDataPage;
